import React from "react";
import {
  Card,
  Elevation,
  H1,
  H3,
  Text,
  Button,
  Intent,
  Alert,
  Callout,
  Spinner,
  Dialog,
  Classes,
  Tooltip,
  InputGroup,
  Icon,
  NonIdealState,
  FormGroup,
  ControlGroup,
  NumericInput,
  Switch,
  HTMLSelect,
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import Papa from "papaparse";
import { apiCallPost, apiCallGet } from "../../../micro/fns/util";
import { inject, observer } from "mobx-react";
import cogoToast from "cogo-toast";

class SurgeryPaymentMaster extends React.Component {
  constructor(props) {
    super(props);
    this.fileInputRef = React.createRef();
    this.gridRef = React.createRef();
    this.state = {
      // Data
      rowData: [],
      surgeryGroups: [], // Add surgery groups data
      insurancePanels: [], // Insurance panels for Payor dropdown

      // Loading states
      isLoading: true,
      isSaving: false,

      // Grid state
      gridApi: null,
      columnApi: null,
      quickFilterText: "",
      paginationPageSize: 200,
      availablePageSizes: [25, 50, 100, 200, 500],

      // Upload status
      uploadStatus: {
        isUploading: false,
        success: false,
        error: null,
        message: "",
      },

      // Audit log
      auditLog: {
        isOpen: false,
        recordId: null,
        recordName: "",
        activities: [],
      },
      auditActivities: {}, // Store all audit activities by recordId
      searchText: "", // Search functionality

      // Edit modal
      editModal: {
        isOpen: false,
        record: null,
        isSaving: false,
      },

      // Add modal
      addModal: {
        isOpen: false,
        isSaving: false,
      },

      // Delete confirmation state
      deleteConfirmation: {
        isOpen: false,
        recordToDelete: null,
      },
    };
  }

  componentDidMount() {
    this.loadData();
    this.loadSurgeryGroups(); // Load surgery groups
    this.loadInsurancePanels(); // Load insurance panels for Payor

    // Add global CSS for button hover effects and dark theme
    const style = document.createElement("style");
    style.textContent = `
      .surgery-payment-button:hover {
        transform: scale(1.02);
        box-shadow: 0 4px 12px rgba(23, 148, 156, 0.3);
      }
      
      .surgery-payment-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0,0,0,0.15);
      }
      
      .surgery-payment-button:active {
        transform: scale(0.98);
      }
      
      .surgery-payment-button:focus {
        outline: 2px solid #17949c;
        outline-offset: 2px;
      }
      
      .surgery-payment-input:focus {
        border-color: #17949c;
        box-shadow: 0 0 0 2px rgba(23, 148, 156, 0.2);
      }
      
      .surgery-payment-success {
        animation: successBounce 0.6s ease-out;
      }
      
      .surgery-payment-error {
        animation: errorShake 0.4s ease-out;
      }
      
      /* AG Grid header text wrapping */
      .ag-header-cell-label {
        white-space: normal !important;
        line-height: 1.3 !important;
        padding: 10px 6px !important;
        text-align: center !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        hyphens: auto !important;
        color: #2b2b2b !important;
        font-weight: 600 !important;
        font-size: 13px !important;
        min-height: 50px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }
      
      .ag-header-cell-text {
        white-space: normal !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        line-height: 1.3 !important;
        color: #2b2b2b !important;
        font-weight: 600 !important;
        font-size: 13px !important;
        text-align: center !important;
      }
      
      .ag-header-cell {
        height: auto !important;
        min-height: 60px !important;
      }
      
      /* Light mode theme fixes for AG Grid */
      .ag-theme-alpine {
        --ag-background-color: #ffffff !important;
        --ag-header-background-color: #f8f9fa !important;
        --ag-odd-row-background-color: #ffffff !important;
        --ag-even-row-background-color: #f8f9fa !important;
        --ag-border-color: #e2e3e3 !important;
        --ag-header-foreground-color: #2b2b2b !important;
        --ag-foreground-color: #2b2b2b !important;
        --ag-secondary-foreground-color: #6d7072 !important;
        --ag-disabled-foreground-color: #a8aaab !important;
        --ag-input-border-color: #e2e3e3 !important;
        --ag-input-focus-border-color: #17949c !important;
        --ag-selected-row-background-color: #c5f3f6 !important;
        --ag-row-hover-color: #f0f0f0 !important;
      }
      
      /* Floating filter inputs */
      .ag-floating-filter-body {
        background-color: #ffffff !important;
      }
      
      .ag-floating-filter-input {
        background-color: #ffffff !important;
        border: 1px solid #e2e3e3 !important;
        color: #2b2b2b !important;
        border-radius: 4px !important;
      }
      
      .ag-floating-filter-input:focus {
        border-color: #17949c !important;
        box-shadow: 0 0 0 2px rgba(23, 148, 156, 0.2) !important;
        outline: none !important;
      }
      
      .ag-floating-filter-button {
        background-color: #ffffff !important;
        border: 1px solid #e2e3e3 !important;
        color: #6d7072 !important;
      }
      
      .ag-floating-filter-button:hover {
        background-color: #f8f9fa !important;
        border-color: #17949c !important;
        color: #17949c !important;
      }
      
      /* Grid cells */
      .ag-cell {
        background-color: #ffffff !important;
        color: #2b2b2b !important;
        border-color: #e2e3e3 !important;
      }
      
      .ag-row-even .ag-cell {
        background-color: #f8f9fa !important;
      }
      
      .ag-row:hover .ag-cell {
        background-color: #f0f0f0 !important;
      }
      
      .ag-row-selected .ag-cell {
        background-color: #c5f3f6 !important;
      }
      
      /* Header styling */
      .ag-header {
        background-color: #f8f9fa !important;
        border-bottom: 1px solid #e2e3e3 !important;
      }
      
      .ag-header-cell {
        background-color: #f8f9fa !important;
        border-right: 1px solid #e2e3e3 !important;
      }
      
      /* Scrollbar styling */
      .ag-body-viewport::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      
      .ag-body-viewport::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
      }
      
      .ag-body-viewport::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
      }
      
      .ag-body-viewport::-webkit-scrollbar-thumb:hover {
        background: #a8a8a8;
      }
      
      /* Comprehensive theme fixes for both light and dark modes */
      .surgery-payment-master {
        background-color: var(--bg-primary, #ffffff) !important;
        color: var(--text-primary, #2b2b2b) !important;
      }
      
      .surgery-payment-master .ag-theme-alpine {
        --ag-background-color: var(--bg-primary, #ffffff) !important;
        --ag-header-background-color: var(--bg-secondary, #f8f9fa) !important;
        --ag-odd-row-background-color: var(--bg-primary, #ffffff) !important;
        --ag-even-row-background-color: var(--bg-secondary, #f8f9fa) !important;
        --ag-border-color: var(--border-color, #e2e3e3) !important;
        --ag-header-foreground-color: var(--text-primary, #2b2b2b) !important;
        --ag-foreground-color: var(--text-primary, #2b2b2b) !important;
        --ag-secondary-foreground-color: var(--text-secondary, #6d7072) !important;
        --ag-disabled-foreground-color: var(--text-disabled, #a8aaab) !important;
        --ag-input-border-color: var(--border-color, #e2e3e3) !important;
        --ag-input-focus-border-color: var(--primary-color, #17949c) !important;
        --ag-selected-row-background-color: var(--primary-light, #c5f3f6) !important;
        --ag-row-hover-color: var(--hover-color, #f0f0f0) !important;
        --ag-cell-horizontal-border: var(--border-color, #e2e3e3) !important;
        --ag-cell-vertical-border: var(--border-color, #e2e3e3) !important;
      }
      
      /* Force light mode for all AG Grid elements */
      .ag-theme-alpine .ag-root-wrapper {
        background-color: var(--bg-primary, #ffffff) !important;
        color: var(--text-primary, #2b2b2b) !important;
      }
      
      .ag-theme-alpine .ag-header {
        background-color: var(--bg-secondary, #f8f9fa) !important;
        color: var(--text-primary, #2b2b2b) !important;
        border-bottom: 1px solid var(--border-color, #e2e3e3) !important;
      }
      
      .ag-theme-alpine .ag-header-cell {
        background-color: var(--bg-secondary, #f8f9fa) !important;
        color: var(--text-primary, #2b2b2b) !important;
        border-right: 1px solid var(--border-color, #e2e3e3) !important;
      }
      
      .ag-theme-alpine .ag-header-cell-text {
        color: var(--text-primary, #2b2b2b) !important;
      }
      
      .ag-theme-alpine .ag-cell {
        background-color: var(--bg-primary, #ffffff) !important;
        color: var(--text-primary, #2b2b2b) !important;
        border-color: var(--border-color, #e2e3e3) !important;
      }
      
      .ag-theme-alpine .ag-row-even .ag-cell {
        background-color: var(--bg-secondary, #f8f9fa) !important;
      }
      
      .ag-theme-alpine .ag-row:hover .ag-cell {
        background-color: var(--hover-color, #f0f0f0) !important;
      }
      
      .ag-theme-alpine .ag-row-selected .ag-cell {
        background-color: var(--primary-light, #c5f3f6) !important;
      }
      
      .ag-theme-alpine .ag-floating-filter-body {
        background-color: var(--bg-primary, #ffffff) !important;
      }
      
      .ag-theme-alpine .ag-floating-filter-input {
        background-color: var(--bg-primary, #ffffff) !important;
        border: 1px solid var(--border-color, #e2e3e3) !important;
        color: var(--text-primary, #2b2b2b) !important;
      }
      
      .ag-theme-alpine .ag-floating-filter-input:focus {
        border-color: var(--primary-color, #17949c) !important;
        box-shadow: 0 0 0 2px rgba(23, 148, 156, 0.2) !important;
      }
      
      .ag-theme-alpine .ag-floating-filter-button {
        background-color: var(--bg-primary, #ffffff) !important;
        border: 1px solid var(--border-color, #e2e3e3) !important;
        color: var(--text-secondary, #6d7072) !important;
      }
      
      .ag-theme-alpine .ag-floating-filter-button:hover {
        background-color: var(--hover-color, #f0f0f0) !important;
        border-color: var(--primary-color, #17949c) !important;
        color: var(--primary-color, #17949c) !important;
      }
      
      /* Override any dark theme styles */
      .ag-theme-alpine .ag-body-viewport {
        background-color: var(--bg-primary, #ffffff) !important;
      }
      
      .ag-theme-alpine .ag-center-cols-container {
        background-color: var(--bg-primary, #ffffff) !important;
      }
      
      .ag-theme-alpine .ag-body-horizontal-scroll {
        background-color: var(--bg-primary, #ffffff) !important;
      }
      
      .ag-theme-alpine .ag-body-vertical-scroll {
        background-color: var(--bg-primary, #ffffff) !important;
      }
      
      /* Ensure text is always readable */
      .ag-theme-alpine * {
        color: inherit !important;
      }
      
      .ag-theme-alpine .ag-cell-value {
        color: var(--text-primary, #2b2b2b) !important;
      }
      
      .ag-theme-alpine .ag-header-cell-label {
        color: var(--text-primary, #2b2b2b) !important;
      }
      
      /* Dark mode specific fixes for header visibility */
      [data-theme="dark"] .ag-theme-alpine .ag-header-cell-label,
      .dark .ag-theme-alpine .ag-header-cell-label,
      .bp3-dark .ag-theme-alpine .ag-header-cell-label {
        color: #2b2b2b !important;
        font-weight: 600 !important;
      }
      
      [data-theme="dark"] .ag-theme-alpine .ag-header-cell-text,
      .dark .ag-theme-alpine .ag-header-cell-text,
      .bp3-dark .ag-theme-alpine .ag-header-cell-text {
        color: #2b2b2b !important;
        font-weight: 600 !important;
      }
      
      [data-theme="dark"] .ag-theme-alpine .ag-header-cell,
      .dark .ag-theme-alpine .ag-header-cell,
      .bp3-dark .ag-theme-alpine .ag-header-cell {
        background-color: #f8f9fa !important;
        color: #2b2b2b !important;
      }
      
      [data-theme="dark"] .ag-theme-alpine .ag-header,
      .dark .ag-theme-alpine .ag-header,
      .bp3-dark .ag-theme-alpine .ag-header {
        background-color: #f8f9fa !important;
        color: #2b2b2b !important;
      }
      
      /* Force dark text on light header background for all themes */
      .ag-theme-alpine .ag-header-cell-label,
      .ag-theme-alpine .ag-header-cell-text,
      .ag-theme-alpine .ag-header-cell-label .ag-header-cell-text {
        color: #2b2b2b !important;
        font-weight: 600 !important;
        text-shadow: none !important;
      }
      
      /* Ensure header text is always dark and visible */
      .ag-theme-alpine .ag-header-cell-label *,
      .ag-theme-alpine .ag-header-cell-text * {
        color: #2b2b2b !important;
        font-weight: 600 !important;
      }
      
      /* Override any inherited light colors */
      .ag-theme-alpine .ag-header-cell-label span,
      .ag-theme-alpine .ag-header-cell-text span {
        color: #2b2b2b !important;
        font-weight: 600 !important;
      }
      
                  /* Nuclear option - force all header text to be dark and visible */
                  .ag-theme-alpine .ag-header-cell *,
                  .ag-theme-alpine .ag-header-cell-label *,
                  .ag-theme-alpine .ag-header-cell-text *,
                  .ag-theme-alpine .ag-header-cell div,
                  .ag-theme-alpine .ag-header-cell span,
                  .ag-theme-alpine .ag-header-cell p {
                    color: #2b2b2b !important;
                    font-weight: 600 !important;
                    opacity: 1 !important;
                  }

                  /* Ensure header text is never transparent or light */
                  .ag-theme-alpine .ag-header-cell {
                    color: #2b2b2b !important;
                  }

                  .ag-theme-alpine .ag-header-cell * {
                    color: #2b2b2b !important;
                  }

                  /* Dark theme overrides for better visibility */
                  .ag-theme-alpine-dark {
                    --ag-background-color: var(--bg-primary, #1a1a1a) !important;
                    --ag-header-background-color: var(--bg-secondary, #2d2d2d) !important;
                    --ag-odd-row-background-color: var(--bg-primary, #1a1a1a) !important;
                    --ag-even-row-background-color: var(--bg-secondary, #2d2d2d) !important;
                    --ag-border-color: var(--border-color, #404040) !important;
                    --ag-header-foreground-color: var(--text-primary, #ffffff) !important;
                    --ag-foreground-color: var(--text-primary, #ffffff) !important;
                    --ag-secondary-foreground-color: var(--text-secondary, #cccccc) !important;
                    --ag-disabled-foreground-color: var(--text-disabled, #888888) !important;
                    --ag-input-border-color: var(--border-color, #404040) !important;
                    --ag-input-focus-border-color: var(--primary-color, #17949c) !important;
                    --ag-selected-row-background-color: var(--primary-light, #0d4a4e) !important;
                    --ag-row-hover-color: var(--hover-color, #333333) !important;
                  }

                  /* Force dark theme for all AG Grid elements */
                  .ag-theme-alpine-dark .ag-root-wrapper {
                    background-color: var(--bg-primary, #1a1a1a) !important;
                    color: var(--text-primary, #ffffff) !important;
                  }

                  .ag-theme-alpine-dark .ag-header {
                    background-color: var(--bg-secondary, #2d2d2d) !important;
                    color: var(--text-primary, #ffffff) !important;
                    border-bottom: 1px solid var(--border-color, #404040) !important;
                  }

                  .ag-theme-alpine-dark .ag-header-cell {
                    background-color: var(--bg-secondary, #2d2d2d) !important;
                    color: var(--text-primary, #ffffff) !important;
                    border-right: 1px solid var(--border-color, #404040) !important;
                  }

                  .ag-theme-alpine-dark .ag-header-cell-text {
                    color: var(--text-primary, #ffffff) !important;
                  }

                  .ag-theme-alpine-dark .ag-cell {
                    background-color: var(--bg-primary, #1a1a1a) !important;
                    color: var(--text-primary, #ffffff) !important;
                    border-color: var(--border-color, #404040) !important;
                  }

                  .ag-theme-alpine-dark .ag-row-even .ag-cell {
                    background-color: var(--bg-secondary, #2d2d2d) !important;
                  }

                  .ag-theme-alpine-dark .ag-row:hover .ag-cell {
                    background-color: var(--hover-color, #333333) !important;
                  }

                  .ag-theme-alpine-dark .ag-row-selected .ag-cell {
                    background-color: var(--primary-light, #0d4a4e) !important;
                  }

                  .ag-theme-alpine-dark .ag-floating-filter-body {
                    background-color: var(--bg-primary, #1a1a1a) !important;
                  }

                  .ag-theme-alpine-dark .ag-floating-filter-input {
                    background-color: var(--bg-primary, #1a1a1a) !important;
                    border: 1px solid var(--border-color, #404040) !important;
                    color: var(--text-primary, #ffffff) !important;
                  }

                  .ag-theme-alpine-dark .ag-floating-filter-input:focus {
                    border-color: var(--primary-color, #17949c) !important;
                    box-shadow: 0 0 0 2px rgba(23, 148, 156, 0.2) !important;
                  }

                  .ag-theme-alpine-dark .ag-floating-filter-button {
                    background-color: var(--bg-primary, #1a1a1a) !important;
                    border: 1px solid var(--border-color, #404040) !important;
                    color: var(--text-secondary, #cccccc) !important;
                  }

                  .ag-theme-alpine-dark .ag-floating-filter-button:hover {
                    background-color: var(--hover-color, #333333) !important;
                    border-color: var(--primary-color, #17949c) !important;
                    color: var(--primary-color, #17949c) !important;
                  }

                  /* Dark theme for edit modal */
                  .bp3-dark .bp3-dialog {
                    background-color: var(--bg-primary, #1a1a1a) !important;
                    color: var(--text-primary, #ffffff) !important;
                  }

                  .bp3-dark .bp3-dialog-header {
                    background-color: var(--bg-secondary, #2d2d2d) !important;
                    color: var(--text-primary, #ffffff) !important;
                    border-bottom: 1px solid var(--border-color, #404040) !important;
                  }

                  .bp3-dark .bp3-dialog-body {
                    background-color: var(--bg-primary, #1a1a1a) !important;
                    color: var(--text-primary, #ffffff) !important;
                  }

                  .bp3-dark .bp3-dialog-footer {
                    background-color: var(--bg-secondary, #2d2d2d) !important;
                    border-top: 1px solid var(--border-color, #404040) !important;
                  }

                  .bp3-dark .bp3-input {
                    background-color: var(--bg-secondary, #2d2d2d) !important;
                    color: var(--text-primary, #ffffff) !important;
                    border: 1px solid var(--border-color, #404040) !important;
                  }

                  .bp3-dark .bp3-input:focus {
                    background-color: var(--bg-secondary, #2d2d2d) !important;
                    color: var(--text-primary, #ffffff) !important;
                    border-color: var(--primary-color, #17949c) !important;
                    box-shadow: 0 0 0 2px rgba(23, 148, 156, 0.2) !important;
                  }

                  .bp3-dark .bp3-numeric-input .bp3-input-group {
                    background-color: var(--bg-secondary, #2d2d2d) !important;
                    color: var(--text-primary, #ffffff) !important;
                    border: 1px solid var(--border-color, #404040) !important;
                  }

                  .bp3-dark .bp3-numeric-input .bp3-input-group:focus-within {
                    border-color: var(--primary-color, #17949c) !important;
                    box-shadow: 0 0 0 2px rgba(23, 148, 156, 0.2) !important;
                  }

                  .bp3-dark .bp3-numeric-input .bp3-input {
                    background-color: transparent !important;
                    color: var(--text-primary, #ffffff) !important;
                  }

                  .bp3-dark .bp3-switch .bp3-control-indicator {
                    background-color: var(--bg-secondary, #2d2d2d) !important;
                    border: 1px solid var(--border-color, #404040) !important;
                  }

                  .bp3-dark .bp3-switch input:checked ~ .bp3-control-indicator {
                    background-color: var(--primary-color, #17949c) !important;
                    border-color: var(--primary-color, #17949c) !important;
                  }

                  .bp3-dark .bp3-form-group .bp3-label {
                    color: var(--text-primary, #ffffff) !important;
                  }

                  .bp3-dark .bp3-form-group .bp3-text-muted {
                    color: var(--text-secondary, #cccccc) !important;
                  }

                  .bp3-dark .bp3-button {
                    background-color: var(--bg-secondary, #2d2d2d) !important;
                    color: var(--text-primary, #ffffff) !important;
                    border: 1px solid var(--border-color, #404040) !important;
                  }

                  .bp3-dark .bp3-button:hover {
                    background-color: var(--hover-color, #333333) !important;
                  }

                  .bp3-dark .bp3-button.bp3-intent-primary {
                    background-color: var(--primary-color, #17949c) !important;
                    color: #ffffff !important;
                    border-color: var(--primary-color, #17949c) !important;
                  }

                  .bp3-dark .bp3-button.bp3-intent-primary:hover {
                    background-color: var(--primary-dark, #0d4a4e) !important;
                  }
      
      @keyframes successBounce {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }
      
      @keyframes errorShake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-5px); }
        75% { transform: translateX(5px); }
      }
      
      @media (prefers-reduced-motion: reduce) {
        .surgery-payment-button:hover,
        .surgery-payment-card:hover {
          transform: none;
        }
        
        .surgery-payment-success,
        .surgery-payment-error {
          animation: none;
        }
      }
    `;
    document.head.appendChild(style);
  }

  loadData = async () => {
    try {
      this.setState({ isLoading: true });
      cogoToast.loading("Loading surgery payment master data...");
      console.log("Loading data with prefix:", this.props.authStore.prefix);
      const apiUrl = `/v3/${this.props.authStore.prefix}/neoSxPaymentMaster/getAll?limit=10000&offset=0&includeInactive=true`;
      console.log("API URL:", apiUrl);
      const response = await apiCallGet(apiUrl);
      const rowData = response?.data?.items || [];

      this.setState({
        rowData: rowData,
        isLoading: false,
      });
      cogoToast.success("Surgery payment master data loaded successfully");
    } catch (error) {
      console.error("Error loading data:", error);
      this.setState({
        rowData: [],
        isLoading: false,
      });
      cogoToast.error("Failed to load surgery payment master data");
    }
  };

  loadSurgeryGroups = async () => {
    try {
      console.log("🔍 DEBUG: Loading surgery groups from API");
      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxSurgeryGroupMaster/getAll`, {});
      this.setState({ surgeryGroups: response || [] });
      console.log("✅ DEBUG: Surgery groups loaded successfully:", (response || []).length);
    } catch (err) {
      console.error("Failed to load surgery groups:", err);
      cogoToast.error("Failed to load surgery groups");
      // Fallback to empty array if API fails
      this.setState({ surgeryGroups: [] });
    }
  };

  loadInsurancePanels = async () => {
    try {
      console.log("🔍 DEBUG: Loading insurance panels from API");
      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxInsurancePanelMaster/getAll`, {});
      const panels = response?.data?.items || response || [];
      this.setState({ insurancePanels: panels });
      console.log("✅ DEBUG: Insurance panels loaded successfully:", panels.length);
    } catch (err) {
      console.error("Failed to load insurance panels:", err);
      cogoToast.error("Failed to load insurance panels");
      this.setState({ insurancePanels: [] });
    }
  };

  testDatabaseConnection = async () => {
    try {
      cogoToast.loading("Testing database connection...", {
        position: "bottom-left",
      });

      const apiUrl = `/v3/${this.props.authStore.prefix}/neoSxPaymentMaster/getAll?limit=1`;
      console.log("Testing connection to:", apiUrl);

      const result = await apiCallGet(apiUrl);
      console.log("Connection test result:", result);

      const recordCount = result?.data?.pagination?.total || 0;
      cogoToast.success(`Database connection successful! Records: ${recordCount}`, { position: "bottom-left" });

      // Log test connection activity
      const auditActivity = {
        action: "Test Connection",
        description: `Database connection test successful. Records found: ${recordCount}`,
        metadata: {
          status: "success",
          recordCount: recordCount,
          timestamp: new Date().toISOString(),
        },
      };

      // Create a test record ID for audit logging
      const testRecordId = `test_connection_${Date.now()}`;
      await this.logAuditActivity(testRecordId, auditActivity);
    } catch (error) {
      console.error("Database connection test failed:", error);
      cogoToast.error(`Database connection failed: ${error.message || "Unknown error"}`, { position: "bottom-left" });

      // Log failed test connection activity
      const auditActivity = {
        action: "Test Connection",
        description: `Database connection test failed: ${error.message || "Unknown error"}`,
        metadata: {
          error: error.message,
          prefix: this.props.authStore.prefix,
          timestamp: new Date().toISOString(),
        },
      };

      // Create a test record ID for audit logging
      const testRecordId = `test_connection_failed_${Date.now()}`;
      await this.logAuditActivity(testRecordId, auditActivity);
    }
  };

  saveData = async (data) => {
    try {
      cogoToast.loading("Saving surgery payment master data...");

      if (!data || data.length === 0) {
        cogoToast.warn("No data to save");
        return;
      }

      // Validate data before saving
      const validationErrors = this.validateBulkData(data);
      if (validationErrors.length > 0) {
        console.error("Validation errors:", validationErrors);
        cogoToast.error(`Validation failed: ${validationErrors.join(", ")}`);
        return;
      }

      // Save each record individually using the new API
      let successCount = 0;
      let errorCount = 0;

      for (const record of data) {
        try {
          if (record.id && record.id.toString().startsWith("temp_")) {
            // This is a new record, create it
            const createData = this.transformRecordForCreate(record);
            await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxPaymentMaster/create`, createData);
            successCount++;
          } else if (record._id) {
            // This is an existing record, update it
            const updateData = this.transformRecordForUpdate(record);
            await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxPaymentMaster/update/${record._id}`, updateData);
            successCount++;
          }
        } catch (recordError) {
          console.error(`Error saving record ${record.id || record._id}:`, recordError);
          errorCount++;
        }
      }

      if (successCount > 0) {
        cogoToast.success(`Successfully saved ${successCount} surgery payment records`);
      }

      if (errorCount > 0) {
        cogoToast.warn(`${errorCount} records failed to save`);
      }

      // Reload data to get updated records
      await this.loadData();

      // Log bulk save activity
      const auditActivity = {
        action: "Bulk Save",
        description: `Bulk save completed for ${successCount} records, ${errorCount} failed`,
        metadata: {
          successCount: successCount,
          errorCount: errorCount,
          totalRecords: data.length,
          timestamp: new Date().toISOString(),
        },
      };

      // Create a bulk save record ID for audit logging
      const bulkSaveRecordId = `bulk_save_${Date.now()}`;
      await this.logAuditActivity(bulkSaveRecordId, auditActivity);
    } catch (error) {
      console.error("Error saving data:", error);
      cogoToast.error("Failed to save surgery payment data");
    }
  };

  addNewRecord = async (record) => {
    try {
      cogoToast.loading("Creating surgery payment record...");
      const createData = this.transformRecordForCreate(record);
      const result = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxPaymentMaster/create`, createData);
      cogoToast.success("Surgery payment record created successfully");
      return result;
    } catch (error) {
      console.error("Error creating record:", error);
      cogoToast.error("Failed to create surgery payment record");
      throw error;
    }
  };

  updateRecord = async (id, record) => {
    try {
      console.log("Updating record with ID:", id);
      console.log("Record data:", record);

      if (!id) {
        throw new Error("Record ID is required for update");
      }

      if (!record || typeof record !== "object") {
        throw new Error("Record data is required for update");
      }

      // Validate record data
      const validationErrors = this.validateBulkData([record]);
      if (validationErrors.length > 0) {
        throw new Error(`Validation failed: ${validationErrors.join(", ")}`);
      }

      const updateData = this.transformRecordForUpdate(record);
      const result = await apiCallPost(
        `/v3/${this.props.authStore.prefix}/neoSxPaymentMaster/update/${id}`,
        updateData
      );

      console.log("Update result:", result);
      cogoToast.success("Surgery payment record updated successfully!");
      return result;
    } catch (error) {
      console.error("Error updating record:", error);
      console.error("Error details:", {
        id,
        record,
        prefix: this.props.authStore.prefix,
        errorMessage: error.message,
        errorStack: error.stack,
      });

      // Enhanced error handling
      let errorMessage = "Failed to update record";
      if (error.response) {
        const status = error.response.status;
        const serverError = error.response.data?.error || error.response.data?.message;

        switch (status) {
          case 400:
            errorMessage = `Invalid data: ${serverError || error.message}`;
            break;
          case 404:
            errorMessage = "Record not found. It may have been deleted.";
            break;
          case 409:
            errorMessage = `Conflict: ${serverError || "Record already exists with this data"}`;
            break;
          case 422:
            errorMessage = `Validation error: ${serverError || error.message}`;
            break;
          default:
            errorMessage = `Update failed: ${serverError || error.message}`;
        }
      } else {
        errorMessage = error.message || "Unknown error occurred during update";
      }

      cogoToast.error(errorMessage);
      throw error;
    }
  };

  showDeleteConfirmation = (record) => {
    this.setState({
      deleteConfirmation: {
        isOpen: true,
        recordToDelete: record,
      },
    });
  };

  hideDeleteConfirmation = () => {
    this.setState({
      deleteConfirmation: {
        isOpen: false,
        recordToDelete: null,
      },
    });
  };

  confirmDelete = () => {
    if (this.state.deleteConfirmation.recordToDelete) {
      const recordId =
        this.state.deleteConfirmation.recordToDelete._id || this.state.deleteConfirmation.recordToDelete.id;
      this.deleteRecord(recordId);
      this.hideDeleteConfirmation();
    }
  };

  deleteRecord = async (id) => {
    try {
      cogoToast.loading("Deleting surgery payment record...");
      await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxPaymentMaster/delete/${id}`, {});

      // Remove from local state
      this.setState((prevState) => ({
        rowData: prevState.rowData.filter((record) => record.id !== id),
      }));

      cogoToast.success("Surgery payment record deleted successfully");
    } catch (error) {
      console.error("Error deleting record:", error);
      cogoToast.error("Failed to delete surgery payment record");
    }
  };

  editRecord = async (record) => {
    console.log("Editing record:", record);
    this.setState({
      editModal: {
        isOpen: true,
        record: { ...record },
        isSaving: false,
      },
    });
  };

  openEditModal = (record) => {
    this.setState({
      editModal: {
        isOpen: true,
        record: { ...record },
        isSaving: false,
      },
    });
  };

  closeEditModal = () => {
    this.setState({
      editModal: {
        isOpen: false,
        record: null,
        isSaving: false,
      },
    });
  };

  updateEditField = (field, value) => {
    this.setState((prevState) => ({
      editModal: {
        ...prevState.editModal,
        record: {
          ...prevState.editModal.record,
          [field]: value,
        },
      },
    }));
  };

  saveEditRecord = async () => {
    const { record } = this.state.editModal;
    if (!record) return;

    try {
      this.setState((prevState) => ({
        editModal: {
          ...prevState.editModal,
          isSaving: true,
        },
      }));

      cogoToast.loading("Saving changes...");

      if (record.id && record.id.toString().startsWith("temp_")) {
        // This is a new record, create it
        const createData = this.transformRecordForCreate(record);
        const result = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxPaymentMaster/create`, createData);

        // Update the local state with the returned ID
        const recordWithId = {
          ...record,
          _id: result?.data?._id || record.id,
        };

        this.setState((prevState) => ({
          rowData: prevState.rowData.map((row) => (row.id === record.id ? recordWithId : row)),
        }));
      } else if (record._id) {
        // This is an existing record, update it
        const updateData = this.transformRecordForUpdate(record);
        const result = await apiCallPost(
          `/v3/${this.props.authStore.prefix}/neoSxPaymentMaster/update/${record._id}`,
          updateData
        );

        // Update the local state
        this.setState((prevState) => ({
          rowData: prevState.rowData.map((row) => (row._id === record._id ? record : row)),
        }));
      }

      cogoToast.success("Record updated successfully");
      this.closeEditModal();
    } catch (error) {
      console.error("Error saving record:", error);
      cogoToast.error("Failed to save record");
    } finally {
      this.setState((prevState) => ({
        editModal: {
          ...prevState.editModal,
          isSaving: false,
        },
      }));
    }
  };

  // Transform record for create API
  transformRecordForCreate = (record) => {
    return {
      surgeryName: record.surgeryName || "",
      printedName: record.printedName || "",
      surgeryType: record.surgeryType || "Cataract Surgery",
      category: record.category || "Routine",
      description: record.description || "",
      group: record.group || "",
      branch: record.branch || "",
      implantInvolved: record.implantInvolved || false,
      payor: record.payor || "Cash",
      cashless: record.cashless || false,
      pricing: {
        surgeryRate: record.surgeryRate || 0,
        tissueProcessing: record.tissueProcessing || 0,
        rate: record.rate || 0,
        taxApplicable: record.taxApplicable || 0,
        implantTax: record.implantTax || 0,
        procedureCost: record.procedureCost || 0,
        implantCost: record.implantCost || 0,
        femto: record.femto || 0,
        currency: "INR",
        discountable: true,
        insuranceCoverage: {
          eligible: true,
          coveragePercentage: 80,
          maxCoverageAmount: 100000,
        },
      },
      discounts: {
        specialDiscount: record.specialDiscount || 0,
        mouDiscount: record.mouDiscount || 0,
        iolDiscount: record.iolDiscount || 0,
      },
      packageInclusions: [],
      additionalCharges: [],
      paymentTerms: {
        advanceRequired: true,
        advancePercentage: 20,
        paymentMethods: ["cash", "card", "upi"],
        refundPolicy: "Standard refund policy",
      },
      doctorCharges: {
        surgeonFee: record.surgeryRate || 0,
        assistantFee: 0,
        anesthesiologistFee: 0,
        consultationFee: 0,
      },
      facilityCharges: {
        operationTheatreFee: record.tissueProcessing || 0,
        equipmentFee: 0,
        nursingFee: 0,
        accommodationFee: 0,
      },
      isActive: record.status === "Active",
      effectiveFrom: new Date().toISOString(),
      effectiveTo: undefined,
    };
  };

  // Transform record for update API
  transformRecordForUpdate = (record) => {
    const updateData = {};

    // Basic fields
    if (record.surgeryName !== undefined) updateData.surgeryName = record.surgeryName;
    if (record.printedName !== undefined) updateData.printedName = record.printedName;
    if (record.surgeryType !== undefined) updateData.surgeryType = record.surgeryType;
    if (record.category !== undefined) updateData.category = record.category;
    if (record.description !== undefined) updateData.description = record.description;
    if (record.group !== undefined) updateData.group = record.group;
    if (record.branch !== undefined) updateData.branch = record.branch;
    if (record.implantInvolved !== undefined) updateData.implantInvolved = record.implantInvolved;
    if (record.payor !== undefined) updateData.payor = record.payor;
    if (record.cashless !== undefined) updateData.cashless = record.cashless;
    if (record.status !== undefined) updateData.isActive = record.status === "Active";

    // Update pricing if any pricing fields changed
    const pricingFields = [
      "surgeryRate",
      "tissueProcessing",
      "rate",
      "taxApplicable",
      "implantTax",
      "procedureCost",
      "implantCost",
      "femto",
    ];
    const hasPricingChanges = pricingFields.some((field) => record[field] !== undefined);

    if (hasPricingChanges) {
      updateData.pricing = {
        surgeryRate: record.surgeryRate || 0,
        tissueProcessing: record.tissueProcessing || 0,
        rate: record.rate || 0,
        taxApplicable: record.taxApplicable || 0,
        implantTax: record.implantTax || 0,
        procedureCost: record.procedureCost || 0,
        implantCost: record.implantCost || 0,
        femto: record.femto || 0,
        currency: "INR",
        discountable: true,
        insuranceCoverage: {
          eligible: true,
          coveragePercentage: 80,
          maxCoverageAmount: 100000,
        },
      };
    }

    // Update discounts if any discount fields changed
    const discountFields = ["specialDiscount", "mouDiscount", "iolDiscount"];
    const hasDiscountChanges = discountFields.some((field) => record[field] !== undefined);

    if (hasDiscountChanges) {
      updateData.discounts = {
        specialDiscount: record.specialDiscount || 0,
        mouDiscount: record.mouDiscount || 0,
        iolDiscount: record.iolDiscount || 0,
      };
    }

    // Update doctor charges
    updateData.doctorCharges = {
      surgeonFee: record.surgeryRate || 0,
      assistantFee: 0,
      anesthesiologistFee: 0,
      consultationFee: 0,
    };

    // Update facility charges
    updateData.facilityCharges = {
      operationTheatreFee: record.tissueProcessing || 0,
      equipmentFee: 0,
      nursingFee: 0,
      accommodationFee: 0,
    };

    // Update payment terms
    updateData.paymentTerms = {
      advanceRequired: true,
      advancePercentage: 20,
      paymentMethods: ["cash", "card", "upi"],
      refundPolicy: "Standard refund policy",
    };

    return updateData;
  };

  onGridReady = (params) => {
    this.setState({ gridApi: params.api, columnApi: params.columnApi });
    params.api.paginationSetPageSize(this.state.paginationPageSize || 50);
    // Ensure the Columns panel stays collapsed by default
    params.api.closeToolPanel();
  };

  handlePageSizeChange = (event) => {
    const newSize = Number(event.target.value);
    this.setState({ paginationPageSize: newSize }, () => {
      if (this.state.gridApi) {
        this.state.gridApi.paginationSetPageSize(newSize);
      }
    });
  };

  columnDefs = [
    {
      headerName: "Printed Name",
      field: "printedName",
      width: 200,
      filter: "agTextColumnFilter",
      pinned: "left",
      editable: false,
    },
    {
      headerName: "Surgery Name",
      field: "surgeryName",
      width: 250,
      filter: "agTextColumnFilter",
      editable: false,
    },
    {
      headerName: "Group",
      field: "group",
      width: 140,
      filter: "agTextColumnFilter",
      editable: false,
    },
    {
      headerName: "Branch",
      field: "branch",
      width: 140,
      filter: "agTextColumnFilter",
      editable: false,
    },
    {
      headerName: "Implant Involved?",
      field: "implantInvolved",
      width: 160,
      cellRenderer: (params) => (params.value ? "Yes" : "No"),
      editable: false,
    },
    {
      headerName: "Payor",
      field: "payor",
      width: 140,
      filter: "agTextColumnFilter",
      editable: false,
    },
    {
      headerName: "Surgery Rate",
      field: "surgeryRate",
      width: 160,
      filter: "agNumberColumnFilter",
      cellClass: "ag-right-aligned-cell",
      cellRenderer: (params) => `₹${params.value?.toLocaleString() || 0}`,
      editable: false,
    },
    // Anaesthetic Charges column removed
    {
      headerName: "Tissue Processing",
      field: "tissueProcessing",
      width: 170,
      filter: "agNumberColumnFilter",
      cellClass: "ag-right-aligned-cell",
      cellRenderer: (params) => `₹${params.value?.toLocaleString() || 0}`,
      editable: false,
    },
    {
      headerName: "Rate",
      field: "rate",
      width: 120,
      filter: "agNumberColumnFilter",
      cellClass: "ag-right-aligned-cell",
      cellRenderer: (params) => `₹${params.value?.toLocaleString() || 0}`,
      editable: false,
    },
    {
      headerName: "Tax Applicable (%)",
      field: "taxApplicable",
      width: 170,
      filter: "agNumberColumnFilter",
      cellClass: "ag-right-aligned-cell",
      cellRenderer: (params) => `${params.value || 0}%`,
      editable: false,
    },
    {
      headerName: "Implant Tax (%)",
      field: "implantTax",
      width: 160,
      filter: "agNumberColumnFilter",
      cellClass: "ag-right-aligned-cell",
      cellRenderer: (params) => `${params.value || 0}%`,
      editable: false,
    },
    {
      headerName: "Procedure Cost",
      field: "procedureCost",
      width: 160,
      filter: "agNumberColumnFilter",
      cellClass: "ag-right-aligned-cell",
      cellRenderer: (params) => `₹${params.value?.toLocaleString() || 0}`,
      editable: false,
    },
    {
      headerName: "Implant Cost",
      field: "implantCost",
      width: 160,
      filter: "agNumberColumnFilter",
      cellClass: "ag-right-aligned-cell",
      cellRenderer: (params) => `₹${params.value?.toLocaleString() || 0}`,
      editable: false,
    },
    {
      headerName: "Femto",
      field: "femto",
      width: 120,
      filter: "agNumberColumnFilter",
      cellClass: "ag-right-aligned-cell",
      cellRenderer: (params) => `₹${params.value?.toLocaleString() || 0}`,
      editable: false,
    },
    {
      headerName: "Special Discount",
      field: "specialDiscount",
      width: 170,
      filter: "agNumberColumnFilter",
      cellClass: "ag-right-aligned-cell",
      cellRenderer: (params) => `₹${params.value?.toLocaleString() || 0}`,
      editable: false,
    },
    {
      headerName: "MOU Discount (%)",
      field: "mouDiscount",
      width: 160,
      filter: "agNumberColumnFilter",
      cellClass: "ag-right-aligned-cell",
      cellRenderer: (params) => `${params.value || 0}%`,
      editable: false,
    },
    {
      headerName: "IOL Discount",
      field: "iolDiscount",
      width: 150,
      filter: "agNumberColumnFilter",
      cellClass: "ag-right-aligned-cell",
      cellRenderer: (params) => `₹${params.value?.toLocaleString() || 0}`,
      editable: false,
    },
    {
      headerName: "Cashless?",
      field: "cashless",
      width: 130,
      cellRenderer: (params) => (params.value ? "Yes" : "No"),
      editable: false,
    },
    {
      headerName: "Status",
      field: "status",
      width: 120,
      cellRenderer: (params) => {
        const isActive = params.value === "Active";
        const span = document.createElement("span");
        span.textContent = isActive ? "Active" : "Inactive";
        span.style.color = isActive ? "#28a745" : "#dc3545";
        span.style.fontWeight = "bold";
        return span;
      },
      editable: false,
    },
    {
      headerName: "Updated By",
      field: "updatedBy",
      width: 140,
      editable: false,
    },
    {
      headerName: "Updated At",
      field: "updatedAt",
      width: 170,
      cellRenderer: (params) => {
        if (!params.value) return "";
        const date = new Date(params.value);
        return date.toLocaleString();
      },
      editable: false,
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 120,
      cellRenderer: (params) => {
        const container = document.createElement("div");
        const editBtn = document.createElement("button");
        const deleteBtn = document.createElement("button");

        editBtn.innerHTML = "✏️";
        editBtn.style.marginRight = "5px";
        editBtn.style.padding = "4px 8px";
        editBtn.style.border = "1px solid #137cbd";
        editBtn.style.backgroundColor = "#137cbd";
        editBtn.style.color = "white";
        editBtn.style.borderRadius = "3px";
        editBtn.style.cursor = "pointer";
        editBtn.onclick = () => this.editRecord(params.data);

        deleteBtn.innerHTML = "🗑️";
        deleteBtn.style.padding = "4px 8px";
        deleteBtn.style.border = "1px solid #db3737";
        deleteBtn.style.backgroundColor = "#db3737";
        deleteBtn.style.color = "white";
        deleteBtn.style.borderRadius = "3px";
        deleteBtn.style.cursor = "pointer";
        deleteBtn.onclick = () => this.showDeleteConfirmation(params.data);

        container.appendChild(editBtn);
        container.appendChild(deleteBtn);

        return container;
      },
      filter: false,
      sortable: false,
    },
  ];

  get defaultColDef() {
    return {
      resizable: true,
      sortable: true,
      filter: true,
      flex: 1,
      minWidth: 100,
      floatingFilter: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    };
  }

  get columnTypes() {
    return {
      // Remove custom numericColumn type as it conflicts with AG Grid defaults
    };
  }

  addNewRow = async () => {
    this.setState({
      addModal: {
        isOpen: true,
        isSaving: false,
      },
    });
  };

  openAddModal = () => {
    this.setState({
      addModal: {
        isOpen: true,
        isSaving: false,
      },
    });
  };

  closeAddModal = () => {
    this.setState({
      addModal: {
        isOpen: false,
        isSaving: false,
      },
    });
  };

  updateAddField = (field, value) => {
    this.setState((prevState) => ({
      addModal: {
        ...prevState.addModal,
        [field]: value,
      },
    }));
  };

  saveAddRecord = async () => {
    try {
      this.setState((prevState) => ({
        addModal: {
          ...prevState.addModal,
          isSaving: true,
        },
      }));

      cogoToast.loading("Creating new surgery payment record...");

      const currentUser = this.props.authStore?.name || this.props.authStore?.id || "Unknown User";

      const newRecord = {
        printedName: this.state.addModal.printedName || "",
        surgeryName: this.state.addModal.surgeryName || "",
        surgeryType: this.state.addModal.surgeryType || "Cataract Surgery",
        category: this.state.addModal.category || "Routine",
        description: this.state.addModal.description || "",
        group: this.state.addModal.group || "",
        branch: this.state.addModal.branch || "",
        implantInvolved: this.state.addModal.implantInvolved || false,
        payor: this.state.addModal.payor || "Cash",
        surgeryRate: this.state.addModal.surgeryRate || 0,
        tissueProcessing: this.state.addModal.tissueProcessing || 0,
        rate: this.state.addModal.rate || 0,
        taxApplicable: this.state.addModal.taxApplicable || 0,
        implantTax: this.state.addModal.implantTax || 0,
        procedureCost: this.state.addModal.procedureCost || 0,
        implantCost: this.state.addModal.implantCost || 0,
        femto: this.state.addModal.femto || 0,
        specialDiscount: this.state.addModal.specialDiscount || 0,
        mouDiscount: this.state.addModal.mouDiscount || 0,
        iolDiscount: this.state.addModal.iolDiscount || 0,
        cashless: this.state.addModal.cashless || false,
        status: this.state.addModal.status || "Active",
        updatedBy: currentUser,
        updatedAt: new Date().toISOString(),
      };

      // Transform for API
      const createData = this.transformRecordForCreate(newRecord);
      const result = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxPaymentMaster/create`, createData);

      // Add to local state with the returned ID
      const recordWithId = {
        ...newRecord,
        _id: result?.data?._id || `temp_${Date.now()}`,
        // Add flattened fields for AG Grid display
        surgeryRate: newRecord.surgeryRate || 0,
        tissueProcessing: newRecord.tissueProcessing || 0,
        rate: newRecord.rate || 0,
        taxApplicable: newRecord.taxApplicable || 0,
        implantTax: newRecord.implantTax || 0,
        procedureCost: newRecord.procedureCost || 0,
        implantCost: newRecord.implantCost || 0,
        femto: newRecord.femto || 0,
        specialDiscount: newRecord.specialDiscount || 0,
        mouDiscount: newRecord.mouDiscount || 0,
        iolDiscount: newRecord.iolDiscount || 0,
        status: newRecord.status || "Active",
      };

      this.setState((prevState) => ({
        rowData: [...prevState.rowData, recordWithId],
      }));

      // Log creation activity
      const auditActivity = {
        action: "Created",
        description: "New surgery payment record created",
        metadata: {
          recordName: newRecord.printedName || "New Record",
          surgeryName: newRecord.surgeryName || "New Surgery",
        },
      };

      await this.logAuditActivity(recordWithId._id, auditActivity);

      cogoToast.success("Surgery payment record created successfully");
      this.closeAddModal();
    } catch (error) {
      console.error("Error creating record:", error);
      cogoToast.error("Failed to create surgery payment record");
    } finally {
      this.setState((prevState) => ({
        addModal: {
          ...prevState.addModal,
          isSaving: false,
        },
      }));
    }
  };

  deleteSelectedRows = () => {
    // This would typically get selected rows from the grid
    // For now, we'll just remove the last row as an example
    if (this.state.rowData.length > 1) {
      const currentUser = this.props.authStore?.name || this.props.authStore?.id || "Unknown User";

      // Mark the row as deleted with audit info
      const rowToDelete = this.state.rowData[this.state.rowData.length - 1];
      const updatedRowData = this.state.rowData.map((row, index) => {
        if (index === this.state.rowData.length - 1) {
          return {
            ...row,
            updatedBy: currentUser,
            updatedAt: new Date().toISOString(),
            action: `Deleted: "${rowToDelete.printedName}" - ${rowToDelete.surgeryName}`,
          };
        }
        return row;
      });

      this.setState({ rowData: updatedRowData.slice(0, -1) });
    }
  };

  saveChanges = async () => {
    console.log("Saving changes:", this.state.rowData);
    await this.saveData(this.state.rowData);
  };

  // CSV Upload Functions
  handleFileUpload = (event) => {
    console.log("File upload triggered");
    const file = event.target.files[0];
    console.log("Selected file:", file);

    if (!file) {
      console.log("No file selected");
      return;
    }

    console.log("File type:", file.type);
    console.log("File name:", file.name);

    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      console.log("Invalid file type");
      this.setState({
        uploadStatus: {
          isUploading: false,
          success: false,
          error: "Please select a valid CSV file",
          message: "",
        },
      });
      return;
    }

    console.log("Starting CSV processing...");
    this.setState({
      uploadStatus: {
        isUploading: true,
        success: false,
        error: null,
        message: "Processing CSV file...",
      },
    });

    console.log("Starting Papa.parse with file:", file);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        console.log("Papa.parse complete callback triggered");
        console.log("CSV parsing results:", results);
        console.log("Results data length:", results.data ? results.data.length : 0);
        console.log("Results errors:", results.errors);

        try {
          const parsedData = await this.validateAndParseCSVData(results.data);
          console.log("Parsed data:", parsedData);

          if (parsedData.length > 0) {
            console.log("Updating grid with parsed data:", parsedData);

            // Update the grid first so user can see the data
            this.setState(
              {
                rowData: parsedData,
                uploadStatus: {
                  isUploading: false,
                  success: true,
                  error: null,
                  message: `Successfully imported ${parsedData.length} records`,
                },
              },
              () => {
                console.log("Grid state updated, rowData length:", this.state.rowData.length);
              }
            );

            // Then try to save to database
            try {
              await this.saveData(parsedData);
            } catch (dbError) {
              console.error("Database save failed:", dbError);
              // Data is still shown in grid, just show warning
              this.setState((prevState) => ({
                uploadStatus: {
                  ...prevState.uploadStatus,
                  message: `Data imported but database save failed: ${dbError.message}`,
                },
              }));
            }
          } else {
            this.setState({
              uploadStatus: {
                isUploading: false,
                success: false,
                error: "No valid data found in CSV file",
                message: "",
              },
            });
          }
        } catch (error) {
          console.error("CSV parsing error:", error);
          this.setState({
            uploadStatus: {
              isUploading: false,
              success: false,
              error: error.message,
              message: "",
            },
          });
        }
      },
      error: (error) => {
        console.error("Papa.parse error:", error);
        this.setState({
          uploadStatus: {
            isUploading: false,
            success: false,
            error: `Error parsing CSV: ${error.message}`,
            message: "",
          },
        });
      },
    });

    // Reset file input
    event.target.value = "";
  };

  validateAndParseCSVData = async (data) => {
    console.log("Raw CSV data:", data);
    console.log("Data length:", data.length);

    const expectedColumns = [
      "Printed Name",
      "Surgery Name",
      "GROUP",
      "BRANCH",
      "Implant Involved?",
      "Payor",
      "Surgery Rate",
      "Tissue Processing",
      "Rate",
      "Tax Applicable (%)",
      "Implant Tax (%)",
      "Procedure Cost",
      "Implant Cost",
      "Femto",
      "Special Discount",
      "MOU Discount",
      "IOL Discount",
      "Cashless?",
    ];

    // Check if all required columns are present
    const csvColumns = Object.keys(data[0] || {});
    console.log("CSV columns found:", csvColumns);

    // Create a mapping of expected columns to actual columns (case-insensitive)
    const columnMapping = {};
    const missingColumns = [];

    // Common column name variations
    const columnVariations = {
      "MOU Discount": [
        "MOU Discount",
        "Mou Discount",
        "mou discount",
        "MOU_Discount",
        "Mou_Discount",
        "MOU Discount (%)",
        "Mou Discount (%)",
        "MOU_Discount_%",
      ],
      "IOL Discount": ["IOL Discount", "Iol Discount", "iol discount", "IOL_Discount", "Iol_Discount"],
      "Special Discount": ["Special Discount", "special discount", "Special_Discount", "special_discount"],
      "Implant Involved?": ["Implant Involved?", "Implant Involved", "implant involved", "Implant_Involved"],
      "Cashless?": ["Cashless?", "Cashless", "cashless", "Cashless_Flag"],
      "Tax Applicable (%)": ["Tax Applicable (%)", "Tax Applicable", "tax applicable", "Tax_Applicable"],
      "Implant Tax (%)": ["Implant Tax (%)", "Implant Tax", "implant tax", "Implant_Tax"],
    };

    expectedColumns.forEach((expectedCol) => {
      let foundColumn = csvColumns.find((csvCol) => csvCol.toLowerCase().trim() === expectedCol.toLowerCase().trim());

      // If not found, try variations
      if (!foundColumn && columnVariations[expectedCol]) {
        foundColumn = csvColumns.find((csvCol) =>
          columnVariations[expectedCol].some(
            (variation) => csvCol.toLowerCase().trim() === variation.toLowerCase().trim()
          )
        );
      }

      if (foundColumn) {
        columnMapping[expectedCol] = foundColumn;
        console.log(`Mapped "${expectedCol}" to "${foundColumn}"`);
      } else {
        missingColumns.push(expectedCol);
      }
    });

    if (missingColumns.length > 0) {
      console.log("Missing columns:", missingColumns);
      console.log("Available columns:", csvColumns);
      throw new Error(
        `Missing required columns: ${missingColumns.join(", ")}. Available columns: ${csvColumns.join(", ")}`
      );
    }

    console.log("AuthStore in CSV parsing:", this.props.authStore);
    console.log("AuthStore name:", this.props.authStore?.name);
    console.log("AuthStore id:", this.props.authStore?.id);

    const currentUser = this.props.authStore?.name || this.props.authStore?.id || "Unknown User";

    console.log("Current user in CSV parsing:", currentUser);

    const parsedData = data.map((row, index) => ({
      id: Date.now() + index,
      printedName: row[columnMapping["Printed Name"]] || "",
      surgeryName: row[columnMapping["Surgery Name"]] || "",
      group: row[columnMapping["GROUP"]] || "",
      branch: row[columnMapping["BRANCH"]] || "",
      implantInvolved: this.parseBoolean(row[columnMapping["Implant Involved?"]]),
      payor: row[columnMapping["Payor"]] || "",
      surgeryRate: parseFloat(row[columnMapping["Surgery Rate"]]) || 0,
      tissueProcessing: parseFloat(row[columnMapping["Tissue Processing"]]) || 0,
      rate: parseFloat(row[columnMapping["Rate"]]) || 0,
      taxApplicable: parseFloat(row[columnMapping["Tax Applicable (%)"]]) || 0,
      implantTax: parseFloat(row[columnMapping["Implant Tax (%)"]]) || 0,
      procedureCost: parseFloat(row[columnMapping["Procedure Cost"]]) || 0,
      implantCost: parseFloat(row[columnMapping["Implant Cost"]]) || 0,
      femto: parseFloat(row[columnMapping["Femto"]]) || 0,
      specialDiscount: parseFloat(row[columnMapping["Special Discount"]]) || 0,
      mouDiscount: parseFloat(row[columnMapping["MOU Discount"]]) || 0,
      iolDiscount: parseFloat(row[columnMapping["IOL Discount"]]) || 0,
      cashless: this.parseBoolean(row[columnMapping["Cashless?"]]),
      status: "Active",
      updatedBy: currentUser,
      updatedAt: new Date().toISOString(),
      action: `Bulk Upload (${data.length} records)`,
    }));

    // Log bulk upload activity for each record
    for (const record of parsedData) {
      const auditActivity = {
        action: "Bulk Upload",
        description: `Bulk Upload (${data.length} records)`,
        metadata: {
          recordName: record.printedName,
          surgeryName: record.surgeryName,
          totalRecords: data.length,
        },
      };
      await this.logAuditActivity(record.id, auditActivity);
    }

    console.log("Final parsed data:", parsedData);
    return parsedData;
  };

  parseBoolean = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      const lowerValue = value.toLowerCase().trim();
      return lowerValue === "yes" || lowerValue === "true" || lowerValue === "1";
    }
    return false;
  };

  validateBulkData = (data) => {
    const errors = [];

    if (!Array.isArray(data)) {
      errors.push("Data must be an array");
      return errors;
    }

    data.forEach((record, index) => {
      // Required field validations
      if (!record.printedName || record.printedName.trim() === "") {
        errors.push(`Row ${index + 1}: Printed Name is required`);
      }

      if (!record.surgeryName || record.surgeryName.trim() === "") {
        errors.push(`Row ${index + 1}: Surgery Name is required`);
      }

      if (!record.branch || record.branch.trim() === "") {
        errors.push(`Row ${index + 1}: Branch is required`);
      }

      // Numeric field validations
      const numericFields = [
        "surgeryRate",
        "tissueProcessing",
        "rate",
        "procedureCost",
        "implantCost",
        "femto",
        "specialDiscount",
        "iolDiscount",
      ];

      numericFields.forEach((field) => {
        const value = record[field];
        if (value !== undefined && value !== null && value !== "") {
          const numValue = parseFloat(value);
          if (isNaN(numValue) || numValue < 0) {
            errors.push(`Row ${index + 1}: ${field} must be a positive number`);
          }
        }
      });

      // Percentage field validations
      const percentageFields = ["taxApplicable", "implantTax", "mouDiscount"];
      percentageFields.forEach((field) => {
        const value = record[field];
        if (value !== undefined && value !== null && value !== "") {
          const numValue = parseFloat(value);
          if (isNaN(numValue) || numValue < 0 || numValue > 100) {
            errors.push(`Row ${index + 1}: ${field} must be between 0 and 100`);
          }
        }
      });

      // Status validation
      if (record.status && !["Active", "Inactive"].includes(record.status)) {
        errors.push(`Row ${index + 1}: Status must be either 'Active' or 'Inactive'`);
      }

      // String length validations
      if (record.printedName && record.printedName.length > 200) {
        errors.push(`Row ${index + 1}: Printed Name must be less than 200 characters`);
      }

      if (record.surgeryName && record.surgeryName.length > 250) {
        errors.push(`Row ${index + 1}: Surgery Name must be less than 250 characters`);
      }
    });

    return errors;
  };

  downloadCSVTemplate = () => {
    const templateData = [
      {
        "Printed Name": "Sample Surgery",
        "Surgery Name": "Cataract Surgery",
        GROUP: "General",
        BRANCH: "Main Branch",
        "Implant Involved?": "Yes",
        Payor: "Insurance",
        "Surgery Rate": "50000",
        // Anaesthetic Charges removed
        "Tissue Processing": "2000",
        Rate: "57000",
        "Tax Applicable (%)": "18",
        "Implant Tax (%)": "12",
        "Procedure Cost": "45000",
        "Implant Cost": "25000",
        Femto: "10000",
        "Special Discount": "5000",
        "MOU Discount": "10",
        "IOL Discount": "1000",
        "Cashless?": "Yes",
      },
      {
        "Printed Name": "Another Surgery",
        "Surgery Name": "LASIK Surgery",
        GROUP: "Specialty",
        BRANCH: "Branch 2",
        "Implant Involved?": "No",
        Payor: "Cash",
        "Surgery Rate": "75000",
        // Anaesthetic Charges removed
        "Tissue Processing": "1500",
        Rate: "79500",
        "Tax Applicable (%)": "18",
        "Implant Tax (%)": "0",
        "Procedure Cost": "70000",
        "Implant Cost": "0",
        Femto: "15000",
        "Special Discount": "2000",
        "MOU Discount": "5",
        "IOL Discount": "0",
        "Cashless?": "No",
      },
    ];

    const csv = Papa.unparse(templateData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "surgery_payment_master_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  exportToCSV = () => {
    const csvData = this.state.rowData.map((row) => ({
      "Printed Name": row.printedName,
      "Surgery Name": row.surgeryName,
      GROUP: row.group,
      BRANCH: row.branch,
      "Implant Involved?": row.implantInvolved ? "Yes" : "No",
      Payor: row.payor,
      "Surgery Rate": row.surgeryRate,
      "Tissue Processing": row.tissueProcessing,
      Rate: row.rate,
      "Tax Applicable (%)": row.taxApplicable,
      "Implant Tax (%)": row.implantTax,
      "Procedure Cost": row.procedureCost,
      "Implant Cost": row.implantCost,
      Femto: row.femto,
      "Special Discount": row.specialDiscount,
      "MOU Discount": row.mouDiscount,
      "IOL Discount": row.iolDiscount,
      "Cashless?": row.cashless ? "Yes" : "No",
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `surgery_payment_master_${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Audit Log Methods
  openAuditLog = async (recordId) => {
    const record = this.state.rowData.find((row) => row.id === recordId);
    if (!record) return;

    try {
      // Load audit activities from database
      const activities = await this.loadAuditActivities(recordId);

      this.setState({
        auditLog: {
          isOpen: true,
          recordId: recordId,
          recordName: `${record.printedName} - ${record.surgeryName}`,
          activities: activities,
        },
      });
    } catch (error) {
      console.error("Error loading audit activities:", error);
      cogoToast.error("Failed to load audit activities");
    }
  };

  closeAuditLog = () => {
    this.setState({
      auditLog: {
        isOpen: false,
        recordId: null,
        recordName: "",
        activities: [],
      },
    });
  };

  // Audit Logging Methods
  logAuditActivity = async (recordId, activity) => {
    try {
      console.log("Logging audit activity:", activity);

      const auditData = {
        _id: `audit_${Date.now()}_${Math.random()}`,
        recordId: recordId,
        timestamp: new Date().toISOString(),
        user: this.props.authStore?.name || this.props.authStore?.id || "Unknown User",
        action: activity.action,
        description: activity.description,
        fieldName: activity.fieldName || null,
        previousValue: activity.previousValue || null,
        newValue: activity.newValue || null,
        metadata: activity.metadata || {},
      };

      // Store in component state
      this.setState((prevState) => ({
        auditActivities: {
          ...prevState.auditActivities,
          [recordId]: [...(prevState.auditActivities[recordId] || []), auditData],
        },
      }));

      // Try to save to database (but don't fail if it doesn't work)
      try {
        const result = await apiCallPost(`/v3/${this.props.authStore.prefix}/auditLog/create`, auditData);
        console.log("Audit activity logged to database:", result);
      } catch (dbError) {
        console.log("Database logging failed, but activity stored in state:", dbError.message);
      }

      return auditData;
    } catch (error) {
      console.error("Error logging audit activity:", error);
      // Don't throw error to prevent breaking the main operation
    }
  };

  loadAuditActivities = async (recordId) => {
    try {
      console.log("Loading audit activities for record:", recordId);

      // First check if we have activities stored in state
      const stateActivities = this.state.auditActivities[recordId];
      if (stateActivities && stateActivities.length > 0) {
        console.log("Found activities in state:", stateActivities);
        return stateActivities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      }

      // Try to load from database
      const result = await apiCallGet(`/v3/${this.props.authStore.prefix}/auditLog/getByRecordId?recordId=${recordId}`);
      console.log("Loaded audit activities from database:", result);

      // If database returns activities, use them and store in state
      if (result && result.length > 0) {
        this.setState((prevState) => ({
          auditActivities: {
            ...prevState.auditActivities,
            [recordId]: result,
          },
        }));
        return result.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      }

      // Fallback: Generate activities from current record data
      console.log("No activities found, generating from current data...");
      const record = this.state.rowData.find((row) => row.id === recordId);
      if (!record) {
        console.log("Record not found in current data");
        return [];
      }

      const fallbackActivities = this.generateFallbackActivities(record);
      console.log("Generated fallback activities:", fallbackActivities);

      // Store fallback activities in state
      this.setState((prevState) => ({
        auditActivities: {
          ...prevState.auditActivities,
          [recordId]: fallbackActivities,
        },
      }));

      return fallbackActivities;
    } catch (error) {
      console.error("Error loading audit activities:", error);

      // Fallback: Generate activities from current record data
      console.log("Database error, generating fallback activities...");
      const record = this.state.rowData.find((row) => row.id === recordId);
      if (!record) {
        console.log("Record not found in current data");
        return [];
      }

      const fallbackActivities = this.generateFallbackActivities(record);
      console.log("Generated fallback activities:", fallbackActivities);

      // Store fallback activities in state
      this.setState((prevState) => ({
        auditActivities: {
          ...prevState.auditActivities,
          [recordId]: fallbackActivities,
        },
      }));

      return fallbackActivities;
    }
  };

  generateFallbackActivities = (record) => {
    const activities = [];

    // Add creation activity
    if (record.action && record.action.includes("Created")) {
      activities.push({
        _id: `fallback_${record.id}_created`,
        recordId: record.id,
        timestamp: record.updatedAt,
        user: record.updatedBy,
        action: "Created",
        description: "Record was created",
        type: "create",
      });
    }

    // Add bulk upload activity
    if (record.action && record.action.includes("Bulk Upload")) {
      activities.push({
        _id: `fallback_${record.id}_bulk`,
        recordId: record.id,
        timestamp: record.updatedAt,
        user: record.updatedBy,
        action: "Bulk Upload",
        description: record.action,
        type: "bulk",
      });
    }

    // Add sample data activity
    if (record.action && record.action.includes("Sample Data")) {
      activities.push({
        _id: `fallback_${record.id}_sample`,
        recordId: record.id,
        timestamp: record.updatedAt,
        user: record.updatedBy,
        action: "Sample Data",
        description: record.action,
        type: "sample",
      });
    }

    // Add test connection activity
    if (record.action && record.action.includes("Test Connection")) {
      activities.push({
        _id: `fallback_${record.id}_test`,
        recordId: record.id,
        timestamp: record.updatedAt,
        user: record.updatedBy,
        action: "Test Connection",
        description: "Database connection test",
        type: "test",
      });
    }

    // Add field update activities
    if (record.action && record.action.includes("Updated")) {
      activities.push({
        _id: `fallback_${record.id}_updated`,
        recordId: record.id,
        timestamp: record.updatedAt,
        user: record.updatedBy,
        action: "Field Updated",
        description: record.action,
        type: "update",
      });
    }

    // Add deletion activity
    if (record.action && record.action.includes("Deleted")) {
      activities.push({
        _id: `fallback_${record.id}_deleted`,
        recordId: record.id,
        timestamp: record.updatedAt,
        user: record.updatedBy,
        action: "Deleted",
        description: record.action,
        type: "delete",
      });
    }

    // If no activities found, create a default one
    if (activities.length === 0) {
      activities.push({
        _id: `fallback_${record.id}_exists`,
        recordId: record.id,
        timestamp: record.updatedAt,
        user: record.updatedBy,
        action: "Record Exists",
        description: "Record is present in the system",
        type: "info",
      });
    }

    // Sort by timestamp (newest first)
    return activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  render() {
    const { rowData, isLoading, quickFilterText } = this.state;

    if (isLoading) {
      return (
        <NonIdealState
          icon={<Spinner />}
          title="Loading Surgery Payment Master..."
          description="Please wait while we load the surgery payment data"
        />
      );
    }

    return (
      <div
        className="bp3-dark"
        style={{
          width: "100%",
          height: "calc(100vh - 40px)",
          padding: "24px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* <div
          style={{
            marginBottom: "32px",
            paddingBottom: "24px",
            borderBottom: "1px solid var(--border-color)",
          }}
        >
          <H1
            style={{
              color: "var(--text-primary)",
              margin: "0 0 8px 0",
              fontSize: "32px",
              fontWeight: "700",
            }}
          >
            Surgery Payment Master
          </H1>
          <Text style={{ color: "var(--text-secondary)", margin: 0, fontSize: "18px" }}>
            Manage surgery payment rates, pricing structures, and billing configurations
          </Text>
        </div> */}

        {/* Upload Status Messages */}
        {this.state.uploadStatus.error && (
          <Alert
            intent={Intent.DANGER}
            onClose={() =>
              this.setState((prev) => ({
                uploadStatus: { ...prev.uploadStatus, error: null },
              }))
            }
            style={{
              marginBottom: "24px",
              borderRadius: "4px",
              border: "1px solid #F44336",
            }}
          >
            {this.state.uploadStatus.error}
          </Alert>
        )}

        {this.state.uploadStatus.success && (
          <Callout
            intent={Intent.SUCCESS}
            icon="tick"
            style={{
              marginBottom: "24px",
              borderRadius: "4px",
              border: "1px solid #4CAF50",
            }}
          >
            {this.state.uploadStatus.message}
          </Callout>
        )}

        <Card
          className="bp3-dark"
          style={{
            padding: "0",
            background: "var(--bg-surface)",
            border: "1px solid var(--border-light)",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "var(--bg-surface)",
            color: "var(--text-primary)",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              padding: "24px",
              borderBottom: "1px solid var(--border-light)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "var(--bg-surface)",
            }}
          >
            <H3
              style={{
                color: "var(--text-primary)",
                margin: 0,
                fontSize: "20px",
                fontWeight: "600",
              }}
            >
              Surgery Payment List
            </H3>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <InputGroup
                placeholder="Search surgery payment records..."
                value={quickFilterText}
                onChange={(e) => this.setState({ quickFilterText: e.target.value })}
                leftIcon={<Icon icon={IconNames.SEARCH} />}
                style={{ width: "300px" }}
              />
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Text style={{ color: "var(--text-secondary)" }}>Rows/page</Text>
                <HTMLSelect
                  value={this.state.paginationPageSize}
                  onChange={this.handlePageSizeChange}
                  style={{ width: "120px" }}
                >
                  {this.state.availablePageSizes.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </HTMLSelect>
              </div>
              <Button
                intent="primary"
                icon={<Icon icon={IconNames.ADD} />}
                onClick={this.addNewRow}
                style={{ marginLeft: "8px" }}
              >
                Add New
              </Button>
            </div>
          </div>

          <div className="ag-theme-alpine-dark" style={{ flex: 1, width: "100%", minHeight: 0 }}>
            <AgGridReact
              columnDefs={this.columnDefs}
              rowData={rowData}
              onGridReady={this.onGridReady}
              quickFilterText={quickFilterText}
              pagination={true}
              paginationPageSize={this.state.paginationPageSize}
              defaultColDef={{
                sortable: true,
                filter: true,
                resizable: true,
              }}
              rowSelection="single"
              enableRangeSelection={true}
              enableCharts={true}
              sideBar={true}
              statusBar={{
                statusPanels: [
                  {
                    statusPanel: "agTotalAndFilteredRowCountComponent",
                    align: "left",
                  },
                ],
              }}
              loading={isLoading}
            />
          </div>
        </Card>

        {/* Audit Log Modal */}
        <Dialog
          isOpen={this.state.auditLog.isOpen}
          onClose={this.closeAuditLog}
          title={`Audit Log - ${this.state.auditLog.recordName}`}
          style={{
            width: "800px",
            borderRadius: "8px",
          }}
        >
          <div className={Classes.DIALOG_BODY} style={{ padding: "24px" }}>
            <div style={{ marginBottom: "24px" }}>
              <h3
                style={{
                  color: "#17949c",
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "600",
                  marginBottom: "8px",
                }}
              >
                Activity History
              </h3>
              <p
                style={{
                  color: "#6d7072",
                  margin: 0,
                  fontSize: "14px",
                  lineHeight: "1.5",
                }}
              >
                Complete audit trail for this record
              </p>
            </div>

            {this.state.auditLog.activities.length === 0 ? (
              <Callout
                intent={Intent.WARNING}
                style={{
                  borderRadius: "4px",
                  border: "1px solid #FF9800",
                  backgroundColor: "#fff3e0",
                }}
              >
                No audit activities found for this record.
              </Callout>
            ) : (
              <div
                style={{
                  maxHeight: "400px",
                  overflowY: "auto",
                  paddingRight: "8px",
                }}
              >
                {this.state.auditLog.activities.map((activity, index) => (
                  <div
                    key={activity._id || index}
                    style={{
                      border: "1px solid #e2e3e3",
                      borderRadius: "8px",
                      padding: "16px",
                      marginBottom: "12px",
                      backgroundColor: "#ffffff",
                      transition: "all 150ms ease-out",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "12px",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <span
                          style={{
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "600",
                            backgroundColor: this.getActivityTypeColor(this.getActivityTypeFromAction(activity.action)),
                            color: "white",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {activity.action}
                        </span>
                        <span
                          style={{
                            fontWeight: "600",
                            color: "#2b2b2b",
                            fontSize: "14px",
                          }}
                        >
                          {activity.user}
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#6d7072",
                          fontWeight: "500",
                        }}
                      >
                        {new Date(activity.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#2b2b2b",
                        lineHeight: "1.5",
                        marginBottom: "8px",
                      }}
                    >
                      {activity.description}
                    </div>
                    {activity.fieldName && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6d7072",
                          marginTop: "8px",
                          padding: "8px 12px",
                          backgroundColor: "#f8f9fa",
                          borderRadius: "4px",
                          border: "1px solid #e2e3e3",
                        }}
                      >
                        <strong>Field:</strong> {activity.fieldName}
                        {activity.previousValue !== null && activity.newValue !== null && (
                          <span style={{ marginLeft: "8px" }}>
                            <strong>Change:</strong> {activity.previousValue} → {activity.newValue}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={Classes.DIALOG_FOOTER} style={{ padding: "16px 24px" }}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Tooltip content="Close audit log dialog" hoverOpenDelay={500}>
                <Button
                  onClick={this.closeAuditLog}
                  intent={Intent.PRIMARY}
                  className="surgery-payment-button"
                  style={{
                    backgroundColor: "#17949c",
                    border: "none",
                    borderRadius: "4px",
                    padding: "8px 16px",
                    fontWeight: "500",
                    transition: "all 150ms ease-out",
                  }}
                >
                  Close
                </Button>
              </Tooltip>
            </div>
          </div>
        </Dialog>

        {/* Edit Modal */}
        <Dialog
          isOpen={this.state.editModal.isOpen}
          onClose={this.closeEditModal}
          title="Edit Surgery Payment Record"
          className="bp3-dark"
          style={{
            width: "900px",
            borderRadius: "8px",
            backgroundColor: "var(--bg-primary)",
            color: "var(--text-primary)",
          }}
        >
          <div
            className={Classes.DIALOG_BODY}
            style={{
              padding: "24px",
              backgroundColor: "var(--bg-primary)",
              color: "var(--text-primary)",
            }}
          >
            {this.state.editModal.record && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                  maxHeight: "500px",
                  overflowY: "auto",
                  backgroundColor: "var(--bg-primary)",
                  color: "var(--text-primary)",
                }}
              >
                {/* Basic Information */}
                <div>
                  <H3
                    style={{
                      color: "var(--text-primary)",
                      margin: "0 0 16px 0",
                      fontSize: "18px",
                      fontWeight: "600",
                    }}
                  >
                    Basic Information
                  </H3>

                  <FormGroup label="Printed Name" labelFor="printedName" labelInfo="(required)">
                    <InputGroup
                      id="printedName"
                      value={this.state.editModal.record.printedName || ""}
                      onChange={(e) => this.updateEditField("printedName", e.target.value)}
                      placeholder="Enter printed name"
                      fill
                    />
                  </FormGroup>

                  <FormGroup label="Surgery Name" labelFor="surgeryName" labelInfo="(required)">
                    <InputGroup
                      id="surgeryName"
                      value={this.state.editModal.record.surgeryName || ""}
                      onChange={(e) => this.updateEditField("surgeryName", e.target.value)}
                      placeholder="Enter surgery name"
                      fill
                    />
                  </FormGroup>

                  <FormGroup label="Group" labelFor="group">
                    <HTMLSelect
                      id="group"
                      value={this.state.editModal.record.group || ""}
                      onChange={(e) => this.updateEditField("group", e.target.value)}
                      fill
                      style={{
                        backgroundColor: "var(--bg-surface)",
                        color: "var(--text-primary)",
                        border: "1px solid var(--border-color)",
                      }}
                    >
                      <option value="">Select Group</option>
                      {this.state.surgeryGroups.map((group) => (
                        <option key={group._id} value={group.groupName}>
                          {group.groupName}
                        </option>
                      ))}
                    </HTMLSelect>
                  </FormGroup>

                  <FormGroup label="Branch" labelFor="branch">
                    <InputGroup
                      id="branch"
                      value={this.state.editModal.record.branch || ""}
                      onChange={(e) => this.updateEditField("branch", e.target.value)}
                      placeholder="Enter branch"
                      fill
                    />
                  </FormGroup>

                  <FormGroup label="Payor" labelFor="payor">
                    <HTMLSelect
                      id="payor"
                      value={this.state.editModal.record.payor || ""}
                      onChange={(e) => this.updateEditField("payor", e.target.value)}
                      fill
                      style={{
                        backgroundColor: "var(--bg-surface)",
                        color: "var(--text-primary)",
                        border: "1px solid var(--border-color)",
                      }}
                    >
                      <option value="">Select Payor</option>
                      {this.state.insurancePanels.map((panel) => (
                        <option key={panel._id} value={panel.panelName}>
                          {panel.panelName}
                        </option>
                      ))}
                    </HTMLSelect>
                  </FormGroup>

                  <FormGroup label="Status" labelFor="status">
                    <ControlGroup>
                      <Switch
                        checked={this.state.editModal.record.status === "Active"}
                        onChange={(e) => this.updateEditField("status", e.target.checked ? "Active" : "Inactive")}
                        label="Active"
                      />
                    </ControlGroup>
                  </FormGroup>
                </div>

                {/* Pricing Information */}
                <div>
                  <H3
                    style={{
                      color: "var(--text-primary)",
                      margin: "0 0 16px 0",
                      fontSize: "18px",
                      fontWeight: "600",
                    }}
                  >
                    Pricing Information
                  </H3>

                  <FormGroup label="Surgery Rate (Rs.)" labelFor="surgeryRate">
                    <NumericInput
                      id="surgeryRate"
                      value={this.state.editModal.record.surgeryRate || 0}
                      onValueChange={(value) => this.updateEditField("surgeryRate", value)}
                      placeholder="Enter surgery rate"
                      fill
                    />
                  </FormGroup>

                  {/* Anaesthetic Charges removed */}

                  <FormGroup label="Tissue Processing (Rs.)" labelFor="tissueProcessing">
                    <NumericInput
                      id="tissueProcessing"
                      value={this.state.editModal.record.tissueProcessing || 0}
                      onValueChange={(value) => this.updateEditField("tissueProcessing", value)}
                      placeholder="Enter tissue processing cost"
                      fill
                    />
                  </FormGroup>

                  <FormGroup label="Rate (Rs.)" labelFor="rate">
                    <NumericInput
                      id="rate"
                      value={this.state.editModal.record.rate || 0}
                      onValueChange={(value) => this.updateEditField("rate", value)}
                      placeholder="Enter total rate"
                      fill
                    />
                  </FormGroup>

                  <FormGroup label="Tax Applicable (%)" labelFor="taxApplicable">
                    <NumericInput
                      id="taxApplicable"
                      value={this.state.editModal.record.taxApplicable || 0}
                      onValueChange={(value) => this.updateEditField("taxApplicable", value)}
                      placeholder="Enter tax percentage"
                      fill
                      min={0}
                      max={100}
                      rightElement={<Text>%</Text>}
                    />
                  </FormGroup>

                  <FormGroup label="Implant Tax (%)" labelFor="implantTax">
                    <NumericInput
                      id="implantTax"
                      value={this.state.editModal.record.implantTax || 0}
                      onValueChange={(value) => this.updateEditField("implantTax", value)}
                      placeholder="Enter implant tax percentage"
                      fill
                      min={0}
                      max={100}
                      rightElement={<Text>%</Text>}
                    />
                  </FormGroup>
                </div>

                {/* Additional Information */}
                <div style={{ gridColumn: "1 / -1" }}>
                  <H3
                    style={{
                      color: "var(--text-primary)",
                      margin: "0 0 16px 0",
                      fontSize: "18px",
                      fontWeight: "600",
                    }}
                  >
                    Additional Information
                  </H3>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "20px",
                    }}
                  >
                    <FormGroup label="Procedure Cost (Rs.)" labelFor="procedureCost">
                      <NumericInput
                        id="procedureCost"
                        value={this.state.editModal.record.procedureCost || 0}
                        onValueChange={(value) => this.updateEditField("procedureCost", value)}
                        placeholder="Enter procedure cost"
                        fill
                      />
                    </FormGroup>

                    <FormGroup label="Implant Cost (Rs.)" labelFor="implantCost">
                      <NumericInput
                        id="implantCost"
                        value={this.state.editModal.record.implantCost || 0}
                        onValueChange={(value) => this.updateEditField("implantCost", value)}
                        placeholder="Enter implant cost"
                        fill
                      />
                    </FormGroup>

                    <FormGroup label="Femto (Rs.)" labelFor="femto">
                      <NumericInput
                        id="femto"
                        value={this.state.editModal.record.femto || 0}
                        onValueChange={(value) => this.updateEditField("femto", value)}
                        placeholder="Enter femto cost"
                        fill
                      />
                    </FormGroup>

                    <FormGroup label="Special Discount (Rs.)" labelFor="specialDiscount">
                      <NumericInput
                        id="specialDiscount"
                        value={this.state.editModal.record.specialDiscount || 0}
                        onValueChange={(value) => this.updateEditField("specialDiscount", value)}
                        placeholder="Enter special discount"
                        fill
                      />
                    </FormGroup>

                    <FormGroup label="MOU Discount (%)" labelFor="mouDiscount">
                      <NumericInput
                        id="mouDiscount"
                        value={this.state.editModal.record.mouDiscount || 0}
                        onValueChange={(value) => this.updateEditField("mouDiscount", value)}
                        placeholder="Enter MOU discount %"
                        fill
                        min={0}
                        max={100}
                        rightElement={<Text>%</Text>}
                      />
                    </FormGroup>

                    <FormGroup label="IOL Discount (Rs.)" labelFor="iolDiscount">
                      <NumericInput
                        id="iolDiscount"
                        value={this.state.editModal.record.iolDiscount || 0}
                        onValueChange={(value) => this.updateEditField("iolDiscount", value)}
                        placeholder="Enter IOL discount"
                        fill
                      />
                    </FormGroup>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "20px",
                      marginTop: "20px",
                    }}
                  >
                    <FormGroup label="Implant Involved" labelFor="implantInvolved">
                      <Switch
                        checked={this.state.editModal.record.implantInvolved || false}
                        onChange={(e) => this.updateEditField("implantInvolved", e.target.checked)}
                        label="Implant Involved"
                      />
                    </FormGroup>

                    <FormGroup label="Cashless" labelFor="cashless">
                      <Switch
                        checked={this.state.editModal.record.cashless || false}
                        onChange={(e) => this.updateEditField("cashless", e.target.checked)}
                        label="Cashless"
                      />
                    </FormGroup>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={Classes.DIALOG_FOOTER} style={{ padding: "16px 24px" }}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button onClick={this.closeEditModal} intent="none" style={{ marginRight: "8px" }}>
                Cancel
              </Button>
              <Button
                onClick={this.saveEditRecord}
                intent="primary"
                loading={this.state.editModal.isSaving}
                icon={<Icon icon={IconNames.FLOPPY_DISK} />}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </Dialog>

        {/* Add Modal */}
        <Dialog
          isOpen={this.state.addModal.isOpen}
          onClose={this.closeAddModal}
          title="Add New Surgery Payment Record"
          className="bp3-dark"
          style={{
            width: "900px",
            borderRadius: "8px",
            backgroundColor: "var(--bg-primary)",
            color: "var(--text-primary)",
          }}
        >
          <div
            className={Classes.DIALOG_BODY}
            style={{
              padding: "24px",
              backgroundColor: "var(--bg-primary)",
              color: "var(--text-primary)",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
                maxHeight: "500px",
                overflowY: "auto",
                backgroundColor: "var(--bg-primary)",
                color: "var(--text-primary)",
              }}
            >
              {/* Basic Information */}
              <div>
                <H3
                  style={{
                    color: "var(--text-primary)",
                    margin: "0 0 16px 0",
                    fontSize: "18px",
                    fontWeight: "600",
                  }}
                >
                  Basic Information
                </H3>

                <FormGroup label="Printed Name" labelFor="addPrintedName" labelInfo="(required)">
                  <InputGroup
                    id="addPrintedName"
                    value={this.state.addModal.printedName || ""}
                    onChange={(e) => this.updateAddField("printedName", e.target.value)}
                    placeholder="Enter printed name"
                    fill
                  />
                </FormGroup>

                <FormGroup label="Surgery Name" labelFor="addSurgeryName" labelInfo="(required)">
                  <InputGroup
                    id="addSurgeryName"
                    value={this.state.addModal.surgeryName || ""}
                    onChange={(e) => this.updateAddField("surgeryName", e.target.value)}
                    placeholder="Enter surgery name"
                    fill
                  />
                </FormGroup>

                <FormGroup label="Group" labelFor="addGroup">
                  <HTMLSelect
                    id="addGroup"
                    value={this.state.addModal.group || ""}
                    onChange={(e) => this.updateAddField("group", e.target.value)}
                    fill
                    style={{
                      backgroundColor: "var(--bg-surface)",
                      color: "var(--text-primary)",
                      border: "1px solid var(--border-color)",
                    }}
                  >
                    <option value="">Select Group</option>
                    {this.state.surgeryGroups.map((group) => (
                      <option key={group._id} value={group.groupName}>
                        {group.groupName}
                      </option>
                    ))}
                  </HTMLSelect>
                </FormGroup>

                <FormGroup label="Branch" labelFor="addBranch">
                  <InputGroup
                    id="addBranch"
                    value={this.state.addModal.branch || ""}
                    onChange={(e) => this.updateAddField("branch", e.target.value)}
                    placeholder="Enter branch"
                    fill
                  />
                </FormGroup>

                <FormGroup label="Payor" labelFor="addPayor">
                  <HTMLSelect
                    id="addPayor"
                    value={this.state.addModal.payor || ""}
                    onChange={(e) => this.updateAddField("payor", e.target.value)}
                    fill
                    style={{
                      backgroundColor: "var(--bg-surface)",
                      color: "var(--text-primary)",
                      border: "1px solid var(--border-color)",
                    }}
                  >
                    <option value="">Select Payor</option>
                    {this.state.insurancePanels.map((panel) => (
                      <option key={panel._id} value={panel.panelName}>
                        {panel.panelName}
                      </option>
                    ))}
                  </HTMLSelect>
                </FormGroup>

                <FormGroup label="Status" labelFor="addStatus">
                  <ControlGroup>
                    <Switch
                      checked={this.state.addModal.status === "Active"}
                      onChange={(e) => this.updateAddField("status", e.target.checked ? "Active" : "Inactive")}
                      label="Active"
                    />
                  </ControlGroup>
                </FormGroup>
              </div>

              {/* Pricing Information */}
              <div>
                <H3
                  style={{
                    color: "var(--text-primary)",
                    margin: "0 0 16px 0",
                    fontSize: "18px",
                    fontWeight: "600",
                  }}
                >
                  Pricing Information
                </H3>

                <FormGroup label="Surgery Rate (Rs.)" labelFor="addSurgeryRate">
                  <NumericInput
                    id="addSurgeryRate"
                    value={this.state.addModal.surgeryRate || 0}
                    onValueChange={(value) => this.updateAddField("surgeryRate", value)}
                    placeholder="Enter surgery rate"
                    fill
                  />
                </FormGroup>

                {/* Anaesthetic Charges removed */}

                <FormGroup label="Tissue Processing (Rs.)" labelFor="addTissueProcessing">
                  <NumericInput
                    id="addTissueProcessing"
                    value={this.state.addModal.tissueProcessing || 0}
                    onValueChange={(value) => this.updateAddField("tissueProcessing", value)}
                    placeholder="Enter tissue processing cost"
                    fill
                  />
                </FormGroup>

                <FormGroup label="Rate (Rs.)" labelFor="addRate">
                  <NumericInput
                    id="addRate"
                    value={this.state.addModal.rate || 0}
                    onValueChange={(value) => this.updateAddField("rate", value)}
                    placeholder="Enter total rate"
                    fill
                  />
                </FormGroup>

                <FormGroup label="Tax Applicable (%)" labelFor="addTaxApplicable">
                  <NumericInput
                    id="addTaxApplicable"
                    value={this.state.addModal.taxApplicable || 0}
                    onValueChange={(value) => this.updateAddField("taxApplicable", value)}
                    placeholder="Enter tax percentage"
                    fill
                    min={0}
                    max={100}
                    rightElement={<Text>%</Text>}
                  />
                </FormGroup>

                <FormGroup label="Implant Tax (%)" labelFor="addImplantTax">
                  <NumericInput
                    id="addImplantTax"
                    value={this.state.addModal.implantTax || 0}
                    onValueChange={(value) => this.updateAddField("implantTax", value)}
                    placeholder="Enter implant tax percentage"
                    fill
                    min={0}
                    max={100}
                    rightElement={<Text>%</Text>}
                  />
                </FormGroup>
              </div>

              {/* Additional Information */}
              <div style={{ gridColumn: "1 / -1" }}>
                <H3
                  style={{
                    color: "var(--text-primary)",
                    margin: "0 0 16px 0",
                    fontSize: "18px",
                    fontWeight: "600",
                  }}
                >
                  Additional Information
                </H3>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "20px",
                  }}
                >
                  <FormGroup label="Procedure Cost (Rs.)" labelFor="addProcedureCost">
                    <NumericInput
                      id="addProcedureCost"
                      value={this.state.addModal.procedureCost || 0}
                      onValueChange={(value) => this.updateAddField("procedureCost", value)}
                      placeholder="Enter procedure cost"
                      fill
                    />
                  </FormGroup>

                  <FormGroup label="Implant Cost (Rs.)" labelFor="addImplantCost">
                    <NumericInput
                      id="addImplantCost"
                      value={this.state.addModal.implantCost || 0}
                      onValueChange={(value) => this.updateAddField("implantCost", value)}
                      placeholder="Enter implant cost"
                      fill
                    />
                  </FormGroup>

                  <FormGroup label="Femto (Rs.)" labelFor="addFemto">
                    <NumericInput
                      id="addFemto"
                      value={this.state.addModal.femto || 0}
                      onValueChange={(value) => this.updateAddField("femto", value)}
                      placeholder="Enter femto cost"
                      fill
                    />
                  </FormGroup>

                  <FormGroup label="Special Discount (Rs.)" labelFor="addSpecialDiscount">
                    <NumericInput
                      id="addSpecialDiscount"
                      value={this.state.addModal.specialDiscount || 0}
                      onValueChange={(value) => this.updateAddField("specialDiscount", value)}
                      placeholder="Enter special discount"
                      fill
                    />
                  </FormGroup>

                  <FormGroup label="MOU Discount (%)" labelFor="addMouDiscount">
                    <NumericInput
                      id="addMouDiscount"
                      value={this.state.addModal.mouDiscount || 0}
                      onValueChange={(value) => this.updateAddField("mouDiscount", value)}
                      placeholder="Enter MOU discount %"
                      fill
                      min={0}
                      max={100}
                      rightElement={<Text>%</Text>}
                    />
                  </FormGroup>

                  <FormGroup label="IOL Discount (Rs.)" labelFor="addIolDiscount">
                    <NumericInput
                      id="addIolDiscount"
                      value={this.state.addModal.iolDiscount || 0}
                      onValueChange={(value) => this.updateAddField("iolDiscount", value)}
                      placeholder="Enter IOL discount"
                      fill
                    />
                  </FormGroup>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "20px",
                    marginTop: "20px",
                  }}
                >
                  <FormGroup label="Implant Involved" labelFor="addImplantInvolved">
                    <Switch
                      checked={this.state.addModal.implantInvolved || false}
                      onChange={(e) => this.updateAddField("implantInvolved", e.target.checked)}
                      label="Implant Involved"
                    />
                  </FormGroup>

                  <FormGroup label="Cashless" labelFor="addCashless">
                    <Switch
                      checked={this.state.addModal.cashless || false}
                      onChange={(e) => this.updateAddField("cashless", e.target.checked)}
                      label="Cashless"
                    />
                  </FormGroup>
                </div>
              </div>
            </div>
          </div>

          <div className={Classes.DIALOG_FOOTER} style={{ padding: "16px 24px" }}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button onClick={this.closeAddModal} intent="none" style={{ marginRight: "8px" }}>
                Cancel
              </Button>
              <Button
                onClick={this.saveAddRecord}
                intent="primary"
                loading={this.state.addModal.isSaving}
                icon={<Icon icon={IconNames.ADD} />}
              >
                Create Record
              </Button>
            </div>
          </div>
        </Dialog>

        {/* Delete Confirmation Alert */}
        <Alert
          isOpen={this.state.deleteConfirmation.isOpen}
          intent={Intent.DANGER}
          icon="trash"
          onCancel={this.hideDeleteConfirmation}
          onConfirm={this.confirmDelete}
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
        >
          <p>Are you sure you want to delete this surgery payment record?</p>
          <p>This action cannot be undone and will permanently remove this payment record from the system.</p>
          {this.state.deleteConfirmation.recordToDelete && (
            <div
              style={{
                marginTop: "12px",
                fontSize: "14px",
                color: "var(--text-secondary)",
              }}
            >
              <strong>Record details:</strong>
              <div style={{ marginTop: "4px" }}>
                Surgery: {this.state.deleteConfirmation.recordToDelete.surgeryName || "N/A"}
              </div>
              <div>Payor: {this.state.deleteConfirmation.recordToDelete.payor || "N/A"}</div>
              <div>Rate: {this.state.deleteConfirmation.recordToDelete.rate || "N/A"}</div>
            </div>
          )}
        </Alert>
      </div>
    );
  }

  getActivityTypeColor = (type) => {
    const colors = {
      create: "#4CAF50",
      update: "#2196F3",
      delete: "#F44336",
      bulk: "#FF9800",
      sample: "#17a2b8",
      test: "#6f42c1",
      info: "#6d7072",
    };
    return colors[type] || "#6d7072";
  };

  getActivityTypeFromAction = (action) => {
    if (action.includes("Created")) return "create";
    if (action.includes("Updated")) return "update";
    if (action.includes("Deleted")) return "delete";
    if (action.includes("Bulk Upload")) return "bulk";
    if (action.includes("Sample Data")) return "sample";
    if (action.includes("Test Connection")) return "test";
    return "info";
  };

  componentWillUnmount() {
    // Clean up global styles
    const styles = document.querySelectorAll("style");
    styles.forEach((style) => {
      if (style.textContent.includes("surgery-payment-button")) {
        style.remove();
      }
    });
  }
}

export default inject("authStore")(observer(SurgeryPaymentMaster));
